import React, { useEffect, useMemo, useState } from "react";
import Modal from "../../ui/Modal";
import { useEnglishText } from "../../../text/useEnglishText.js";
import TextField from "@mui/material/TextField";
import Checkbox from "@mui/material/Checkbox";

import {
  getCustomerUserPermissions,
  updateCustomerUserPermission,
} from "../../../services/customerService";

// ---------------------- Helpers -----------------------

const emptyForm = {
  ROLE_TITLE: "",
  DESCRIPTION: "",
};

// تبدیل نام فیلد پرمیشن به لیبل خوانا
const formatPermissionLabel = (key) => {
  if (!key) return "";

  return key
    .toLowerCase()
    .split("_")
    .map((p) => p.charAt(0).toUpperCase() + p.slice(1))
    .join(" ");
};

// نرمال‌سازی مقدار permission
const normalizePermissionValue = (value) => {
  if (value === 1 || value === true || value === "true") return true;

  return false;
};

// استخراج کلیدهای permission
const extractPermissionKeys = (data) => {
  if (!data) return [];

  const excluded = [
    "CP_CUSTOMER_USER_PERMISSION_ID",
    "CP_CUSTOMER_USER_ROLE_DEFINITION_ID",
    "ROLE_TITLE",
    "DESCRIPTION",
  ];
  return Object.keys(data).filter((key) => {
    if (excluded.includes(key)) return false;

    if (key.endsWith("_ID")) return false;

    return true;
  });
};

// -------------------------------------------------------

const RoleModal = ({ open, initialValue, onClose, onSaved }) => {
  const { t } = useEnglishText();

  const [form, setForm] = useState(emptyForm);

  const [saving, setSaving] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");

  // Permissions
  const [permissions, setPermissions] = useState({});
  const [loadingPermissions, setLoadingPermissions] = useState(false);

  // ---------------------- Load Data ----------------------

  useEffect(() => {
    if (!open) return;

    setSubmitted(false);
    setError("");

    if (initialValue) {
      setForm({
        ROLE_TITLE: initialValue.ROLE_TITLE || "",
        DESCRIPTION: initialValue.DESCRIPTION || "",
      });
    } else {
      setForm(emptyForm);
    }

    loadPermissions();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initialValue]);

  // ---------------------- Load Permissions ----------------------

  const loadPermissions = async () => {
    setLoadingPermissions(true);

    try {
      const roleId = initialValue?.CP_CUSTOMER_USER_ROLE_DEFINITION_ID || null;

      const res = await getCustomerUserPermissions(roleId);

      const permissionData = Array.isArray(res) ? res[0] : res;

      if (!permissionData) {
        setPermissions({});
        return;
      }

      const normalized = { ...permissionData };

      Object.keys(permissionData).forEach((key) => {
        if (
          key.endsWith("_ID") ||
          key === "ROLE_TITLE" ||
          key === "DESCRIPTION"
        ) {
          return;
        }

        const value = permissionData[key];

        normalized[key] = value === "true" || value === 1 || value === "1";
      });

      setPermissions(normalized);
    } catch (err) {
      console.error("Error loading permissions:", err);
      setPermissions({});
    } finally {
      setLoadingPermissions(false);
    }
  };

  // ---------------------- Permission Keys ----------------------

  const permissionKeys = useMemo(() => {
    return extractPermissionKeys(permissions);
  }, [permissions]);

  // ---------------------- Validation ----------------------

  const validation = useMemo(() => {
    const errs = {};

    if (!form.ROLE_TITLE.trim()) {
      errs.ROLE_TITLE = t("settings.roles.validation.titleRequired");
    }

    return errs;
  }, [form, t]);

  const isValid = Object.keys(validation).length === 0;

  // ---------------------- Handlers ----------------------

  const change = (name, value) => {
    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (submitted) {
      setError("");
    }
  };

  const togglePermission = (key) => {
    setPermissions((prev) => ({
      ...prev,
      [key]: !normalizePermissionValue(prev[key]),
    }));
  };

  // ---------------------- Submit ----------------------

  const onSubmit = async (e) => {
    e.preventDefault();

    setSubmitted(true);

    if (!isValid) return;

    setSaving(true);

    try {
      const payload = {
        cp_customer_user_role_definition_id:
          initialValue?.CP_CUSTOMER_USER_ROLE_DEFINITION_ID ||
          permissions?.CP_CUSTOMER_USER_ROLE_DEFINITION_ID,

        role_title: form.ROLE_TITLE,
        description: form.DESCRIPTION,
      };

      permissionKeys.forEach((key) => {
        payload[key.toLowerCase()] = normalizePermissionValue(permissions[key])
          ? "1"
          : "0";
      });

      await updateCustomerUserPermission(payload);

      onSaved?.();
    } catch (err) {
      console.error(err);

      setError(
        err?.response?.data?.message || err?.message || "Error saving role",
      );
    } finally {
      setSaving(false);
    }
  };

  // ---------------------- UI ----------------------

  return (
    <Modal
      open={open}
      title={
        initialValue
          ? t("settings.roles.editRole")
          : t("settings.roles.addRole")
      }
      onClose={onClose}
    >
      <form className="profile-modal__form" onSubmit={onSubmit}>
        {error && <div className="profile-modal__error">{error}</div>}

        {/* Form Fields */}
        <div
          style={{
            display: "grid",
            gap: "16px",
            gridTemplateColumns: "1fr 1fr",
          }}
        >
          <TextField
            label={t("settings.roles.fields.title")}
            value={form.ROLE_TITLE}
            onChange={(e) => change("ROLE_TITLE", e.target.value)}
            error={submitted && !!validation.ROLE_TITLE}
            helperText={submitted && validation.ROLE_TITLE}
            size="small"
            fullWidth
          />

          <TextField
            label={t("settings.roles.fields.description")}
            value={form.DESCRIPTION}
            onChange={(e) => change("DESCRIPTION", e.target.value)}
            size="small"
            fullWidth
          />
        </div>

        {/* Permissions */}
        <div className="permissions-section">
          <h3 className="permissions-section__title">
            {t("settings.roles.permissionsTitle", "Permissions")}
          </h3>

          {loadingPermissions ? (
            <div>{t("common.loading")}</div>
          ) : permissionKeys.length === 0 ? (
            <div>{t("common.noData")}</div>
          ) : (
            <div className="permissions-list">
              {permissionKeys.map((key) => (
                <label key={key} className="permission-item">
                  <Checkbox
                    checked={normalizePermissionValue(permissions[key])}
                    onChange={() => togglePermission(key)}
                  />

                  <span>{formatPermissionLabel(key)}</span>
                </label>
              ))}
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="profile-modal__actions">
          <button
            type="button"
            className="btn btn--secondary"
            onClick={onClose}
          >
            {t("common.cancel")}
          </button>

          <button type="submit" className="btn btn--primary" disabled={saving}>
            {saving ? t("common.saving") : t("common.save")}
          </button>
        </div>
      </form>
    </Modal>
  );
};

export default RoleModal;
